import { FileText, User, Building2, ChevronRight, MoreHorizontal } from 'lucide-react';
import {
  useResearchBreadcrumbs,
  type BreadcrumbEntityType,
} from '@/contexts/ResearchBreadcrumbContext';
import { cn } from '@/lib/utils';

const ENTITY_ICONS: Record<BreadcrumbEntityType, React.ComponentType<{ className?: string }>> = {
  bill: FileText,
  legislator: User,
  committee: Building2,
};

const MOBILE_VISIBLE_COUNT = 3;

export function ResearchBreadcrumbs() {
  const { trail, popTo } = useResearchBreadcrumbs();

  if (trail.length < 2) return null;

  const hasCollapsed = trail.length > MOBILE_VISIBLE_COUNT;
  const mobileStartIndex = trail.length - MOBILE_VISIBLE_COUNT;

  return (
    <nav
      aria-label="Research trail"
      className="w-full border-b border-border bg-muted/30 px-4 py-1.5 overflow-x-auto"
    >
      <ol className="flex items-center gap-1 text-sm whitespace-nowrap">
        {trail.map((entry, index) => {
          const Icon = ENTITY_ICONS[entry.entityType];
          const isLast = index === trail.length - 1;
          const isHiddenOnMobile = hasCollapsed && index < mobileStartIndex;

          return (
            <li
              key={`${entry.entityType}-${entry.entityId}`}
              className={cn('flex items-center gap-1', isHiddenOnMobile && 'hidden md:flex')}
            >
              {/* Show ellipsis before first visible mobile crumb */}
              {hasCollapsed && index === mobileStartIndex && (
                <span className="flex items-center gap-1 md:hidden" aria-hidden="true">
                  <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                  <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                </span>
              )}

              {isLast ? (
                <span className="flex items-center gap-1 text-foreground font-medium truncate max-w-[200px]">
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  {entry.label}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => popTo(index)}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors truncate max-w-[200px]"
                  >
                    <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                    {entry.label}
                  </button>
                  <ChevronRight className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
                </>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
